import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getPayments } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import EmptyState from '../components/ui/EmptyState'
import Avatar from '../components/ui/Avatar'

export default function Payments() {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getPayments({ take: 9999 })
      .then((res) => setPayments(res.data.payments || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(payments, {
    defaultSort: 'date', defaultDir: 'desc', pageSize: 25, storageKey: 'payments',
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Payments</h1>
          <p className="text-sm text-gray-500 mt-0.5">Payments are recorded from invoice detail pages.</p>
        </div>
      </div>

      <div className="card overflow-hidden !p-0">
        {loading ? (
          <div className="space-y-3 p-6">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : payments.length === 0 ? (
          <EmptyState type="payments" message="No payments recorded yet." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50/80 border-b">
                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Invoice" sortKey="invoice.number" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Customer" sortKey="invoice.customer.name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Method" sortKey="method" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Amount" sortKey="amount" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    <th className="px-4 py-3 font-medium">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-gray-600">{formatDate(p.date)}</td>
                      <td className="px-4 py-3">
                        <Link to={`/invoices/${p.invoiceId}`} className="text-brand-600 hover:underline font-medium">
                          {p.invoice?.number || '—'}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={p.invoice?.customer?.name} />
                          <span className="text-gray-600">{p.invoice?.customer?.name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-500 text-xs bg-gray-100 px-2 py-0.5 rounded">{p.method?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">{formatCurrency(p.amount)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{p.referenceNo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
